import * as THREE from 'three';
import type { CharacterDef } from '../game/content';
import { playerHit } from '../game/rules';
import { heroModel } from './models';

export const DASH_COOLDOWN = 2.4;
const DASH_TIME = 0.17;
const DASH_SPEED = 24;
const ACCEL = 14;

export class Player {
  readonly group = new THREE.Group();
  private readonly rig = new THREE.Group();
  private body: THREE.Mesh | null = null;
  private xray: THREE.Mesh | null = null;
  private orb: THREE.Mesh | null = null;
  readonly light: THREE.PointLight;
  private readonly marker: THREE.Mesh;
  vx = 0;
  vz = 0;
  faceX = 0;
  faceZ = 1;
  hp = 100;
  maxHp = 100;
  healMult = 1;
  readonly radius = 0.45;
  invuln = 0;
  dashCd = 0;
  dashTime = 0;
  private dashX = 0;
  private dashZ = 0;
  private hurtTimer = 0;
  private moving = 0;

  constructor() {
    this.light = new THREE.PointLight('#8fb8ff', 9, 9, 1.6);
    this.light.position.set(0.46, 2.2, 0.16);
    this.marker = new THREE.Mesh(
      new THREE.RingGeometry(0.55, 0.68, 40).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: '#8fb8ff', transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    this.marker.position.y = 0.05;
    this.group.add(this.rig, this.light, this.marker);
  }

  get x(): number {
    return this.group.position.x;
  }

  get z(): number {
    return this.group.position.z;
  }

  get dashing(): boolean {
    return this.dashTime > 0;
  }

  setCharacter(def: CharacterDef): void {
    if (this.body) {
      this.rig.remove(this.body);
      this.body.geometry.dispose();
      (this.body.material as THREE.Material).dispose();
    }
    if (this.xray) {
      this.rig.remove(this.xray);
      (this.xray.material as THREE.Material).dispose();
    }
    if (this.orb) {
      this.rig.remove(this.orb);
      this.orb.geometry.dispose();
      (this.orb.material as THREE.Material).dispose();
    }
    const model = heroModel(def.robe, def.trim);
    this.body = model.body;
    this.orb = model.orb;
    // Silhouette drawn only where scenery hides the hero (depth test inverted).
    this.xray = new THREE.Mesh(
      model.body.geometry,
      new THREE.MeshBasicMaterial({ color: def.trim, transparent: true, opacity: 0.45, depthWrite: false, depthFunc: THREE.GreaterDepth }),
    );
    this.xray.renderOrder = 20;
    this.rig.add(this.body, this.orb, this.xray);
    this.light.color.set(def.trim);
    (this.marker.material as THREE.MeshBasicMaterial).color.set(def.trim);
  }

  reset(maxHp: number, healMult = 1): void {
    this.healMult = healMult;
    this.group.position.set(0, 0, 0);
    this.vx = this.vz = 0;
    this.faceX = 0;
    this.faceZ = 1;
    this.maxHp = maxHp;
    this.hp = maxHp;
    this.invuln = 1;
    this.dashCd = 0;
    this.dashTime = 0;
    this.hurtTimer = 0;
  }

  // Returns true when a dash started this frame.
  update(dt: number, time: number, moveX: number, moveZ: number, dash: boolean, speed: number, pushX: number, pushZ: number): boolean {
    this.invuln = Math.max(0, this.invuln - dt);
    this.dashCd = Math.max(0, this.dashCd - dt);
    this.hurtTimer = Math.max(0, this.hurtTimer - dt);
    const len = Math.hypot(moveX, moveZ);
    if (len > 0.05) {
      this.faceX = moveX / len;
      this.faceZ = moveZ / len;
    }
    let started = false;
    if (dash && this.dashCd <= 0) {
      this.dashTime = DASH_TIME;
      this.dashCd = DASH_COOLDOWN;
      this.dashX = this.faceX;
      this.dashZ = this.faceZ;
      this.invuln = Math.max(this.invuln, DASH_TIME + 0.15);
      started = true;
    }
    if (this.dashTime > 0) {
      this.dashTime -= dt;
      this.vx = this.dashX * DASH_SPEED;
      this.vz = this.dashZ * DASH_SPEED;
    } else {
      const k = 1 - Math.exp(-ACCEL * dt);
      this.vx += (moveX * speed - this.vx) * k;
      this.vz += (moveZ * speed - this.vz) * k;
    }
    this.group.position.x += (this.vx + pushX) * dt;
    this.group.position.z += (this.vz + pushZ) * dt;

    // Presentation: face movement, bob, lean, pulse the staff orb.
    this.moving += ((len > 0.05 ? 1 : 0) - this.moving) * Math.min(1, dt * 8);
    const target = Math.atan2(this.faceX, this.faceZ);
    let diff = target - this.rig.rotation.y;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    this.rig.rotation.y += diff * Math.min(1, dt * 14);
    this.rig.position.y = Math.abs(Math.sin(time * 9)) * 0.08 * this.moving;
    this.rig.rotation.x = 0.12 * this.moving;
    if (this.orb) this.orb.scale.setScalar(1 + Math.sin(time * 6) * 0.15);
    this.light.intensity = 8 + Math.sin(time * 6) * 1.5;
    const blink = this.invuln > 0 && this.hurtTimer > 0 ? (Math.sin(time * 50) > 0 ? 0.35 : 1) : 1;
    this.rig.visible = blink > 0.5 || this.dashTime > 0;
    (this.marker.material as THREE.MeshBasicMaterial).opacity = this.dashCd <= 0 ? 0.85 : 0.3;
    return started;
  }

  // Applies armour and i-frames (none for a hazard tick); returns damage actually taken.
  hurt(amount: number, armor: number, iframes = true): number {
    const hit = playerHit(this.invuln, amount, armor, iframes);
    if (hit.dmg <= 0) return 0;
    this.hp -= hit.dmg;
    this.invuln = hit.invuln;
    if (iframes) this.hurtTimer = 0.5;
    return hit.dmg;
  }

  heal(amount: number): void {
    if (amount <= 0) return;
    this.hp = Math.min(this.maxHp, this.hp + amount * this.healMult);
  }
}
